import {
  ArrowPathIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  SparklesIcon,
  StopIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AiArtifact,
  AiConversationMessage,
  AssistantMessage,
  AssistantStep,
  getAiConversationMessages,
  getAssistantModel,
  newAssistantTurnId,
  postAssistantPrompt,
  stopAssistantPrompt,
  streamAssistantPrompt,
  uploadAiProjectKnowledge,
} from 'services'
import { MarkdownRender } from 'stories/components/MarkdownRender/MarkdownRender'

import {
  AssistantStepList,
  mergeStep,
  normalizeStep,
  StepElapsed,
} from '../../components/AssistantModal/AssistantSteps'

/** A finished turn's steps, collapsed - the transcript is prose first, the work behind it on ask. */
function ProjectMessageSteps({ steps }: { steps?: Array<string | AssistantStep> }) {
  const [open, setOpen] = useState(false)
  if (!steps?.length) return null
  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-shade-blue"
      >
        <ChevronRightIcon className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        {open ? 'Hide steps' : `Show steps (${steps.length})`}
      </button>
      {open && <AssistantStepList steps={steps.map(normalizeStep)} className="mt-1" />}
    </div>
  )
}

/** How much of the thread is replayed to the model. The backend also carries project context. */
const HISTORY_TURNS = 20

const ACCEPTED_FILE_TYPES = '.pdf,.docx,.txt,.md,.csv,.tsv,.json,.xlsx,.xls,.rtf,.html,.xml'

interface ProjectChatProps {
  projectId: number
  conversationId: number
  /** The loan this thread is about; sent with every turn so loan-scoped tools default to it. */
  loanNumber?: number
  /** Artifacts in the project, so a turn that produced one can link to it by name. */
  artifacts: AiArtifact[]
  onArtifactsChanged: () => void
  onKnowledgeChanged: () => void
  onOpenArtifact: (artifactId: number) => void
  /** The thread was renamed server-side from its first message. */
  onConversationChanged: () => void
  /**
   * A turn asked for from outside the composer - Run project, in the header. Sent once per `nonce`,
   * so pressing Run again with the same words runs it again; a turn already in flight wins and the
   * request is dropped, rather than two agents writing into one thread.
   */
  injectedPrompt?: { text: string; nonce: number }
  /** Raised once an injected prompt has been sent, so the host can stop holding it. */
  onInjectedPromptSent?: () => void
}

export function ProjectChat({
  projectId,
  conversationId,
  loanNumber,
  artifacts,
  onArtifactsChanged,
  onKnowledgeChanged,
  onOpenArtifact,
  onConversationChanged,
  injectedPrompt,
  onInjectedPromptSent,
}: ProjectChatProps) {
  const [messages, setMessages] = useState<AiConversationMessage[]>([])
  const [input, setInput] = useState('')
  const [isRunning, setRunning] = useState(false)
  const [isUploading, setUploading] = useState(false)
  const [isLoading, setLoading] = useState(true)
  // The model answering here, shown under the composer: predicted on mount, then replaced by the
  // one each turn reports, since the assistant can fall back to another path mid-turn.
  const [model, setModel] = useState('')
  // What the turn in flight is doing, filled in as each tool call starts and again when it returns.
  // A project run is the long kind - several reads, a document or two, then the reasoning - so
  // watching it work is the difference between "it is thinking" and knowing it read the right loan.
  const [liveSteps, setLiveSteps] = useState<AssistantStep[]>([])
  const [runningStatus, setRunningStatus] = useState('')
  /** The answer so far while it is still being written; '' when nothing is streaming. */
  const [streamingText, setStreamingText] = useState('')
  /** Aborts the turn in flight when the user presses Stop; the server is told separately. */
  const abortRef = useRef<AbortController | null>(null)
  /** The id of the turn in flight, so Stop can name it to the server. */
  const turnIdRef = useRef('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLoading(true)
    getAiConversationMessages(conversationId)
      .then((data) => setMessages(data || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false))
  }, [conversationId])

  useEffect(() => {
    getAssistantModel()
      .then((info) => setModel(info.model || info.engine))
      .catch(() => {})
  }, [])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: isRunning ? 'smooth' : 'auto' })
  }, [messages, isRunning])

  const onSend = async (asked?: string) => {
    const prompt = (asked ?? input).trim()
    if (!prompt || isRunning) return

    // Optimistic user turn; the server persists the real row and we reload nothing on success.
    const optimistic: AiConversationMessage = {
      id: -Date.now(),
      conversationId,
      role: 'user',
      content: prompt,
      files: [],
      artifactIds: [],
      createdAt: new Date().toISOString(),
    }
    const history: AssistantMessage[] = messages.slice(-HISTORY_TURNS).map((message) => ({
      id: String(message.id),
      role: message.role,
      content: message.content,
      createdAt: message.createdAt,
      // Carry the turn's tool calls and their results, so a follow-up question sees what was
      // actually looked up rather than only the paragraph written about it.
      blocks: message.blocks?.length ? message.blocks : undefined,
    }))

    setMessages((prev) => [...prev, optimistic])
    if (asked === undefined) setInput('')
    setRunning(true)
    abortRef.current = new AbortController()
    // Names this turn so Stop can reach it on the server; aborting our own request no longer stops
    // the work, which is what makes a reload harmless.
    const turnId = newAssistantTurnId()
    turnIdRef.current = turnId

    const requestBody = {
      prompt,
      history,
      context: 'project-assistant',
      projectId,
      conversationId,
      loanNumber,
      turnId,
    }
    // A step arrives twice - once when the call starts, once with its result - so the second one
    // updates the row in place instead of adding a duplicate.
    const localSteps: AssistantStep[] = []
    const recordStep = (step: AssistantStep) => {
      setLiveSteps([...mergeStep(localSteps, step)])
    }
    setLiveSteps([])
    setRunningStatus('')
    setStreamingText('')
    // Deltas arrive faster than React re-renders, so the text is accumulated here and pushed into
    // state, rather than appended to whatever the last committed render happened to hold.
    let streamedText = ''
    const recordDelta = (delta: { text?: string; reset?: boolean }) => {
      // A new step of the agent loop: what came before was narration before a tool call, and its
      // step card already records it. Start the answer over instead of gluing them together.
      if (delta.reset) streamedText = ''
      if (delta.text) streamedText += delta.text
      setStreamingText(streamedText)
    }

    try {
      // Stream so the panel shows the work as it happens. If streaming fails for any reason (a
      // proxy that buffers, a transient network error) the plain endpoint still completes the
      // turn - just without the live view.
      let response
      try {
        response = await streamAssistantPrompt(requestBody, {
          onStatus: setRunningStatus,
          onStep: recordStep,
          onDelta: recordDelta,
          signal: abortRef.current?.signal,
        })
      } catch (streamErr: any) {
        // A turn the user stopped is finished, not failed: keep what was written and do not run the
        // whole question again on the non-streaming endpoint.
        if (streamErr?.name === 'AbortError') {
          const partial = streamedText.trim()
          if (partial) {
            setMessages((prev) => [
              ...prev,
              {
                id: -Date.now() - 3,
                conversationId,
                role: 'model',
                content: `${partial}\n\n_Stopped._`,
                files: [],
                artifactIds: [],
                steps: localSteps.length ? localSteps : undefined,
                createdAt: new Date().toISOString(),
              },
            ])
          }
          return
        }
        setRunningStatus('')
        setStreamingText('')
        // A dropped connection is not a stopped turn: the server carries the turn through and saves
        // it. By the time any step has run, the assistant may already have WRITTEN something - a
        // payment, a note, a field - so re-posting the same question would perform those writes a
        // second time, and the original turn is still going to save its own answer on top. Retry on
        // the plain endpoint only while nothing has started yet; after that, recover rather than
        // re-run. The floating panel already behaves this way.
        if (localSteps.length || streamedText.trim()) {
          const refreshed = await getAiConversationMessages(conversationId).catch(() => null)
          const saved = Array.isArray(refreshed) ? refreshed : []
          const finished = saved.length > 0 && saved[saved.length - 1]?.role === 'model'
          if (finished) {
            setMessages(saved)
          } else {
            const base = saved.length ? saved : messages
            setMessages([
              ...base,
              {
                id: -Date.now() - 4,
                conversationId,
                role: 'model',
                content:
                  'The connection dropped while the assistant was working. It carries on ' +
                  'server-side, so reopen this conversation in a moment to see the finished answer.',
                files: [],
                artifactIds: [],
                steps: localSteps.length ? localSteps : undefined,
                createdAt: new Date().toISOString(),
              } as any,
            ])
          }
          return
        }
        response = await postAssistantPrompt(requestBody)
      }
      const data: any = response?.data
      const content = typeof data === 'string' ? data : data?.content || ''
      if (response?.model) setModel(response.model)
      const steps = response?.steps?.length ? response.steps : localSteps
      setMessages((prev) => [
        ...prev,
        {
          id: -Date.now() - 1,
          conversationId,
          role: 'model',
          content,
          files: [],
          artifactIds: (response?.artifacts || []).map((artifact) => artifact.id),
          steps: steps.length ? steps : undefined,
          blocks: response?.blocks?.length ? response.blocks : undefined,
          createdAt: new Date().toISOString(),
        },
      ])
      if (response?.artifacts?.length) onArtifactsChanged()
      if (messages.length === 0) onConversationChanged()
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: -Date.now() - 2,
          conversationId,
          role: 'model',
          content: 'Something went wrong answering that. Please try again.',
          files: [],
          artifactIds: [],
          createdAt: new Date().toISOString(),
        },
      ])
    } finally {
      setRunning(false)
      setLiveSteps([])
      setRunningStatus('')
      // The finished message carries the answer now; leaving the live copy up would show it twice.
      setStreamingText('')
      abortRef.current = null
      turnIdRef.current = ''
    }
  }

  /**
   * Send a prompt the header asked for. Keyed on the nonce rather than the text, so pressing Run
   * twice runs it twice; the host is told either way, so a request dropped because a turn is
   * already in flight does not leave it waiting on a send that will never come.
   */
  const sentNonceRef = useRef(0)
  useEffect(() => {
    if (isLoading) return
    if (!injectedPrompt?.nonce || injectedPrompt.nonce === sentNonceRef.current) return
    sentNonceRef.current = injectedPrompt.nonce
    onSend(injectedPrompt.text)
    onInjectedPromptSent?.()
    // eslint-disable-next-line
  }, [injectedPrompt?.nonce, isLoading])

  /**
   * Files attached here go into the project's knowledge rather than riding along with a single
   * message: knowledge is what every conversation in the project can see, which is what a user
   * dropping a document into a project actually wants.
   */
  const onSelectFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length) return

    setUploading(true)
    try {
      const added = await uploadAiProjectKnowledge(projectId, files)
      onKnowledgeChanged()
      const unreadable = (added || []).filter((item) => item.status !== 'ready')
      toast(
        `Added ${added?.length || 0} file(s) to project knowledge.` +
          (unreadable.length ? ` ${unreadable.length} could not be read as text.` : ''),
        { type: unreadable.length ? 'warning' : 'success' },
      )
    } catch {
      // The global API handler surfaces the error.
    } finally {
      setUploading(false)
    }
  }

  const artifactTitle = (id: number) => artifacts.find((artifact) => artifact.id === id)?.title || `Artifact ${id}`

  return (
    <div className="flex flex-col h-full min-h-0">
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {!isLoading && !messages.length && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-shade-blue/10">
              <SparklesIcon className="h-6 w-6 text-shade-blue" />
            </div>
            <p className="font-semibold text-gray-700">Start the conversation</p>
            <p className="text-sm mt-1 max-w-sm">
              This project&apos;s instructions and knowledge are already loaded. Ask a question, or ask for a
              document and it will be saved as an artifact.
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {/*
              The two roles are shaped differently on purpose, the way a chat reads best: what you
              said is a compact bubble on the right, what the assistant wrote is full-width prose on
              the page. Answers here run to headings, tables and code, and a narrow tinted bubble
              makes all of that harder to read.
            */}
            <div
              className={
                message.role === 'user'
                  ? 'max-w-[80%] rounded-2xl rounded-br-sm bg-shade-blue px-4 py-2.5 text-sm text-white'
                  : 'w-full max-w-none text-sm text-gray-800'
              }
            >
              {message.role === 'user' ? (
                <p className="whitespace-pre-wrap">{message.content}</p>
              ) : (
                <>
                  <ProjectMessageSteps steps={message.steps} />
                  <MarkdownRender text={message.content} />
                </>
              )}
              {!!message.artifactIds?.length && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {message.artifactIds.map((id) => (
                    <button
                      key={id}
                      onClick={() => onOpenArtifact(id)}
                      className="flex items-center gap-1 rounded-md border border-shade-blue/30 bg-white px-2 py-1 text-xs text-shade-blue hover:bg-shade-blue/5"
                    >
                      <DocumentTextIcon className="h-3 w-3" />
                      {artifactTitle(id)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {isRunning && (
          <div className="text-gray-500">
            {/* Everything except the step in flight; that one is the bouncing line below. */}
            <AssistantStepList steps={liveSteps.slice(0, -1)} className="mb-1.5" />
            {streamingText ? (
              // The answer as it is written. The dots are for the wait before the first word; once
              // there are words, they are the better progress indicator.
              <div className="text-sm text-gray-800">
                <MarkdownRender text={streamingText} />
                <span className="ml-0.5 inline-block h-4 w-[3px] animate-pulse rounded-sm bg-shade-blue/70 align-text-bottom" />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-shade-blue/60 [animation-delay:-0.3s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-shade-blue/60 [animation-delay:-0.15s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-shade-blue/60" />
                </div>
                <span className="text-sm">{runningStatus || 'Working on it...'}</span>
                {/* Counts up while the phrase stands, so a long silence reads as work, not a stall. */}
                <StepElapsed resetKey={runningStatus} className="text-xs text-gray-400" />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
        <div className="relative flex items-end rounded-lg border border-gray-300 bg-white focus-within:border-shade-blue focus-within:ring-1 focus-within:ring-shade-blue/30 transition-all">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_TYPES}
            className="hidden"
            onChange={onSelectFiles}
          />
          <button
            type="button"
            className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-shade-blue disabled:cursor-not-allowed disabled:opacity-50"
            title="Add files to project knowledge"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PaperClipIcon className="h-4 w-4" />}
          </button>
          <textarea
            value={input}
            rows={1}
            placeholder="Ask anything about this project..."
            className="flex-1 resize-none border-0 bg-transparent px-1 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 max-h-32"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onSend()
              }
            }}
          />
          {isRunning ? (
            <button
              onClick={() => {
                // Server first: an aborted request is no longer a stop on its own.
                if (turnIdRef.current) stopAssistantPrompt(turnIdRef.current)
                abortRef.current?.abort()
              }}
              title="Stop"
              aria-label="Stop generating"
              className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-800 text-white hover:bg-gray-700"
            >
              <StopIcon className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => onSend()}
              disabled={isUploading || !input.trim()}
              className="m-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-shade-blue text-white hover:bg-shade-blue/90 disabled:bg-gray-200 disabled:text-gray-400"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-3 text-xs text-gray-400">
          <p>Attached files are added to this project&apos;s knowledge.</p>
          {!!model && <span className="shrink-0 truncate">{model}</span>}
        </div>
      </div>
    </div>
  )
}
